import * as React from "react"

/* ============================================================
 *  HONEST SECTIONS   (FCS-124)
 * ============================================================
 *  A design composed in the Template Maker was written with some
 *  business's own words in it: its address, its opening hours, its
 *  reviews, its claims ("veteran owned", "licensed & insured"), its
 *  trade. sections.js draws those slots exactly as authored, and the
 *  bindings only replace the few things the host knows (name, phone,
 *  services, pictures). Everything else would be published, under a
 *  contractor's own name, as theirs.
 *
 *  So for the section shapes below the page is drawn from WHAT THE
 *  CONTRACTOR TOLD US and nothing else:
 *
 *    - a slot with no answer is not drawn (no empty box, no stand-in)
 *    - a section with nothing to say is not drawn, and the header's
 *      links are made from the sections that are
 *    - a repeating thing is drawn once per real item (two services
 *      make two cards, not two plus two of somebody else's)
 *    - nothing is claimed that nobody said: no rating, no years, no
 *      "licensed", no reviews that nobody wrote
 *    - buttons are links that go somewhere (a call, an email, the
 *      quote form), never a picture of a button
 *
 *  It uses the same markup and class names as the shapes it replaces,
 *  so the design's stylesheet, palette and polish apply unchanged: the
 *  LOOK is the Template Maker's, the WORDS are the contractor's.
 *
 *  A shape that is not in HONEST falls back to sections.js. A new
 *  design that uses one must be read for leftover copy before it is
 *  offered: scripts/check-template-import.js in the host does it.
 * ============================================================ */

const clean = (value) => String(value ?? "").trim()
const list = (value) => (Array.isArray(value) ? value : [])
const pad = (n) => String(n).padStart(2, "0")

function initialsOf(name) {
  return String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("")
}

/** Where "get a quote" goes: the form if there is one, else the contact details, else nowhere (and no button). */
const quoteTarget = (view) => (view.wantsForm ? "#quote" : view.hasPhone || view.hasEmail ? "#contact" : "")

const hasContact = (view) => Boolean(view.hasPhone || view.hasEmail)

/** The gallery: the Lead Generation gallery when it is on, else the pictures the builder collected. */
const shots = (view) => (list(view.shots).length ? list(view.shots) : list(view.gallery)).filter(Boolean)

const reviews = (view) => list(view.reviews).filter((r) => r && clean(r.quote))
const faqs = (view) => list(view.faqs).filter((f) => f && clean(f.q) && clean(f.a))
const hours = (view) => list(view.hours).filter((h) => h && clean(h.text))

/** The links to the sections that are on the page, and the button that opens them on a phone. */
function Menu({ nav }) {
  const [open, setOpen] = React.useState(false)
  const links = nav.filter((link) => link.enabled)
  return (
    <>
      <nav className="tm-header-links" data-open={open ? "true" : undefined}>
        {links.map((link) => (
          <a key={link.nodeId} href={link.href} onClick={() => setOpen(false)}>
            {link.label}
          </a>
        ))}
      </nav>
      {links.length ? (
        <button type="button" className="tm-header-burger" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} onClick={() => setOpen((was) => !was)}>
          {open ? "✕" : "☰"}
        </button>
      ) : null}
    </>
  )
}

function photoSpan(src, className = "") {
  return (
    <span
      className={["tm-photo", className].filter(Boolean).join(" ")}
      data-empty={src ? undefined : "true"}
      role={src ? "img" : undefined}
      aria-label={src ? "" : undefined}
      style={src ? { backgroundImage: `url("${src}")` } : undefined}
    />
  )
}

export const HONEST = {
  /* ── the header: name, links, phone, one button ───────────────── */
  "header-bar": {
    show: () => true,
    render: ({ view, nav }) => {
      const left = clean(view.areaLine)
      const row = hours(view)[0]
      const right = row ? `${row.day}: ${row.text}` : ""
      const target = quoteTarget(view)
      const under = clean(view.slogan) || (view.tradeSlug ? clean(view.trade) : "")
      return (
        <div className="tm-header-bar">
          {left || right ? (
            <div className="tm-header-topbar">
              <span>{left ? `◎ ${left}` : ""}</span>
              {right ? <span>◷ {right}</span> : null}
            </div>
          ) : null}
          <div className="tm-header-nav">
            <span className="tm-header-brand">
              <span className="tm-monogram tm-header-logo" aria-hidden="true">
                {view.initials || initialsOf(view.name)}
              </span>
              <span>
                <strong>{view.name}</strong>
                {under ? <small>{under}</small> : null}
              </span>
            </span>
            <Menu nav={nav} />
            {view.hasPhone ? (
              <a className="tm-header-phone" href={view.phoneHref}>
                <small>Call us</small>
                <strong>{view.phone}</strong>
              </a>
            ) : null}
            {target ? (
              <a className="tm-preview-cta" href={target}>
                Get a free quote ↗
              </a>
            ) : null}
          </div>
        </div>
      )
    },
  },

  /* ── the hero: who they are, how to reach them, the real form ──── */
  "hero-benefits-form": {
    show: () => true,
    backdrop: "side",
    render: ({ view, QuoteForm }) => {
      const target = quoteTarget(view)
      const eyebrow = [view.tradeSlug ? clean(view.trade) : "", clean(view.serviceArea)].filter(Boolean).join(" · ")
      const sub = clean(view.slogan) || clean(view.aboutShort)
      return (
        <>
          <div className="tm-section-content">
            {eyebrow ? <span className="tm-section-eyebrow">{eyebrow}</span> : null}
            <h2>{view.name}</h2>
            {sub ? <p>{sub}</p> : null}
            {target || view.hasPhone ? (
              <span className="tm-section-actions">
                {target ? (
                  <a className="tm-preview-cta" href={target}>
                    Get a free quote
                  </a>
                ) : null}
                {view.hasPhone ? (
                  <a className="tm-preview-cta is-ghost" href={view.phoneHref}>
                    {view.phone}
                  </a>
                ) : null}
              </span>
            ) : null}
          </div>
          {view.wantsForm && QuoteForm ? (
            <div className="tm-form-card is-tall" id="quote">
              <span className="tm-form-kicker">Request a quote</span>
              <strong>Tell us about your project.</strong>
              {/* The host's form is styled under its own `.mk` root (port.css), so it is drawn inside one. */}
              <div className="mk">
                <QuoteForm view={view} />
              </div>
            </div>
          ) : null}
        </>
      )
    },
  },

  /* ── claims nobody made: not drawn ─────────────────────────────── */
  "band-icon-notes": { show: () => false },

  /* ── services: one card per service the contractor listed ──────── */
  "services-numbered-cards": {
    show: (view) => list(view.services).length > 0,
    render: ({ view }) => {
      const items = list(view.services).slice(0, 8)
      const target = quoteTarget(view)
      return (
        <>
          <div className="tm-heading-split">
            <div className="tm-section-content">
              <span className="tm-section-eyebrow">Our services</span>
              <h2>{view.tradeSlug ? `${clean(view.trade)} services` : "What we do"}</h2>
            </div>
          </div>
          <div className="tm-split-cards">
            {items.map((service, i) => (
              <article className="tm-split-card" key={`${service.slug || service.title}-${i}`}>
                <span className="tm-split-card-media">
                  {photoSpan(service.image)}
                  <i aria-hidden="true">{pad(i + 1)}</i>
                </span>
                <span className="tm-split-card-body">
                  <i className="tm-split-card-icon" aria-hidden="true">
                    ◆
                  </i>
                  <strong>{service.title}</strong>
                  {clean(service.description) ? <small>{service.description}</small> : null}
                  {target ? (
                    <a className="tm-text-link" href={target}>
                      Get a quote →
                    </a>
                  ) : null}
                </span>
              </article>
            ))}
          </div>
        </>
      )
    },
  },

  /* ── their own photographs, and only those ─────────────────────── */
  "gallery-filtered": {
    show: (view) => shots(view).length > 0,
    render: ({ view }) => (
      <>
        <div className="tm-section-content">
          <span className="tm-section-eyebrow">Our work</span>
          <h2>Recent work</h2>
        </div>
        <div className="tm-work-grid">
          {shots(view)
            .slice(0, 9)
            .map((src, i) => (
              <span className="tm-work-tile" key={i}>
                {photoSpan(src)}
              </span>
            ))}
        </div>
      </>
    ),
  },

  /* ── about: their words, only when they wrote some ─────────────── */
  "about-local-badge": {
    show: (view) => Boolean(clean(view.aboutText)),
    render: ({ view }) => {
      const target = quoteTarget(view)
      return (
        <>
          {view.aboutImage ? <div className="tm-media-stack">{photoSpan(view.aboutImage, "is-tall")}</div> : null}
          <div className="tm-section-content">
            <span className="tm-section-eyebrow">About us</span>
            <h2>{`About ${view.name}`}</h2>
            <p>{view.aboutText}</p>
            {clean(view.areaLine) ? <p>{view.areaLine}</p> : null}
            {target ? (
              <a className="tm-preview-cta" href={target}>
                Get in touch ↗
              </a>
            ) : null}
          </div>
        </>
      )
    },
  },

  /* ── reviews: only ones a customer wrote, no stars nobody gave ─── */
  "testimonial-carousel": {
    show: (view) => reviews(view).length > 0,
    render: ({ view }) => (
      <>
        <div className="tm-carousel-head">
          <div className="tm-section-content">
            <span className="tm-section-eyebrow">Reviews</span>
            <h2>What customers say</h2>
          </div>
        </div>
        <div className="tm-carousel">
          {reviews(view)
            .slice(0, 6)
            .map((review, i) => (
              <div className="tm-review-card" key={i}>
                <span className="tm-review-top">
                  <em aria-hidden="true">“</em>
                </span>
                <p>{review.quote}</p>
                {clean(review.name) ? (
                  <span className="tm-review-by">
                    <span className="tm-avatar" aria-hidden="true">
                      {initialsOf(review.name) || "–"}
                    </span>
                    <span>
                      <strong>{review.name}</strong>
                    </span>
                  </span>
                ) : null}
              </div>
            ))}
        </div>
      </>
    ),
  },

  /* ── contact: how to reach them, their hours, their questions ──── */
  "contact-showroom-faq": {
    show: (view) => hasContact(view) || hours(view).length > 0 || faqs(view).length > 0,
    render: ({ view }) => (
      <>
        <div className="tm-section-content">
          <span className="tm-section-eyebrow">Contact</span>
          <h2>Get in touch</h2>
          {hasContact(view) || hours(view).length ? (
            <span className="tm-detail-list">
              {view.hasPhone ? (
                <i>
                  <a href={view.phoneHref}>{view.phone}</a>
                </i>
              ) : null}
              {view.hasEmail ? (
                <i>
                  <a href={view.emailHref}>{view.email}</a>
                </i>
              ) : null}
              {hours(view).map((row, i) => (
                <i key={`${row.day}-${i}`}>
                  {row.day}: {row.text}
                </i>
              ))}
            </span>
          ) : null}
          {clean(view.areaLine) ? <p>{view.areaLine}</p> : null}
        </div>
        {faqs(view).length ? (
          <div className="tm-faq">
            <span className="tm-section-eyebrow">Questions</span>
            {faqs(view)
              .slice(0, 8)
              .map((faq, i) => (
                <details className="tm-faq-row" key={i}>
                  <summary>
                    <strong>
                      {faq.q}
                      <i aria-hidden="true">+</i>
                    </strong>
                  </summary>
                  <small>{faq.a}</small>
                </details>
              ))}
          </div>
        ) : null}
      </>
    ),
  },

  /* ── the last call to action: a real call, a real email ───────── */
  "cta-photo-phone": {
    show: (view) => hasContact(view),
    backdrop: "centred",
    render: ({ view }) => {
      const target = quoteTarget(view)
      return (
        <div className="tm-section-content is-centered">
          <h2>Ready to get started?</h2>
          <span className="tm-section-actions">
            {view.hasPhone ? (
              <a className="tm-preview-cta" href={view.phoneHref}>
                Call {view.phone}
              </a>
            ) : null}
            {target ? (
              <a className="tm-preview-cta is-ghost" href={target}>
                Get a free quote
              </a>
            ) : view.hasEmail ? (
              <a className="tm-preview-cta is-ghost" href={view.emailHref}>
                Email us
              </a>
            ) : null}
          </span>
        </div>
      )
    },
  },

  /* ── the footer: who, how to reach them, where, and the year ──── */
  "footer-explore": {
    show: () => true,
    render: ({ view }) => (
      <div className="tm-footer-grid">
        <div>
          <strong>{view.name}</strong>
          {clean(view.slogan) ? <small>{view.slogan}</small> : null}
        </div>
        {hasContact(view) ? (
          <div>
            <strong>Contact</strong>
            {view.hasPhone ? (
              <small>
                <a href={view.phoneHref}>{view.phone}</a>
              </small>
            ) : null}
            {view.hasEmail ? (
              <small>
                <a href={view.emailHref}>{view.email}</a>
              </small>
            ) : null}
          </div>
        ) : null}
        {clean(view.areaLine) ? (
          <div>
            <strong>Service area</strong>
            <small>{view.areaLine}</small>
          </div>
        ) : null}
        <span className="tm-footer-bottom">{`© ${view.year} ${view.name}. All rights reserved.`}</span>
      </div>
    ),
  },
}

/** The section's own picture, as its backdrop, from the contractor's hero photograph only: the design's own stock photograph is not theirs. */
export function honestBackdrop(variant, view) {
  const entry = HONEST[variant]
  if (!entry || !entry.backdrop || !view || !view.heroImage) return undefined
  const overlay = entry.backdrop === "centred" ? "var(--hero-overlay-centred)" : "var(--hero-overlay)"
  return { backgroundImage: `${overlay}, url("${view.heroImage}")` }
}

/** Whether a section is on the page at all. Shapes that are not honest here are always on (they keep their authored words). */
export function isShown(section, view) {
  const entry = HONEST[section.variant]
  return !entry || entry.show(view || {})
}
